import {
  CreateDocumentInput,
  DocumentAssociationsInput,
  DocumentInput2,
  DocumentType,
} from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useDocumentReview } from "providers/documentReview";
import { useCallback, useMemo } from "react";
import { useRetryDocumentExtractions } from "routes/Customer/Documents/hooks/useRetryDocumentExtractionsMutation";

/**
 * Creates uploads already linked to the opener's facilities, then queues their
 * analysis. Linking first keeps the analysis from guessing a facility, and goes
 * through the same facility permission checks as any other upload. The
 * carry-forward flag stays off until the review, once the type is known. If
 * analysis can't be queued, the created documents are removed again.
 */
export async function createLinkedDocumentsAndQueueAnalysis({
  tenantId,
  uploads,
  facilityIds,
  upsertDocuments,
  queueAnalysis,
  deleteDocuments,
}: {
  tenantId: string;
  uploads: CreateDocumentInput[];
  facilityIds: string[];
  upsertDocuments: (params: {
    documents: DocumentInput2[];
    associations: DocumentAssociationsInput[];
    currentSubmissionDocument: boolean;
  }) => Promise<Array<{ id: string }> | undefined>;
  queueAnalysis: (documentIds: string[]) => Promise<string | undefined>;
  deleteDocuments: (documentIds: string[]) => Promise<unknown>;
}): Promise<{ jobId: string; documentIds: string[] }> {
  const created = await upsertDocuments({
    documents: uploads.map((upload) => ({
      ...upload,
      tenantId,
      documentType: DocumentType.Other,
      isArchived: false,
    })),
    associations: facilityIds.map((facilityId) => ({ tenantId, facilityId })),
    currentSubmissionDocument: false,
  });
  const documentIds = (created ?? []).map(({ id }) => id);
  if (documentIds.length !== uploads.length) {
    throw new Error("Some documents failed to save. Please try again.");
  }

  try {
    const jobId = await queueAnalysis(documentIds);
    if (!jobId) {
      throw new Error("Failed to get job ID from queue response");
    }
    return { jobId, documentIds };
  } catch (error) {
    await deleteDocuments(documentIds).catch(() => undefined);
    throw error;
  }
}

/**
 * Upload pieces for an AI upload opened from a facility's page: the facility
 * links for new documents, queueing analysis of documents created with them, and
 * handing the opener's carry-forward choice to the review.
 */
export function useFacilityLinkedUpload({
  tenantId,
  facilityIds,
  carryForwardByDefault,
  upsertDocuments,
}: {
  tenantId: string;
  facilityIds: string[];
  carryForwardByDefault: boolean | undefined;
  upsertDocuments: Parameters<typeof createLinkedDocumentsAndQueueAnalysis>[0]["upsertDocuments"];
}) {
  const { markCarryForward } = useDocumentReview();
  const [queueExistingAnalysis] = useRetryDocumentExtractions();
  const [deleteDocument] = useDeleteDocumentMutation();

  const handOffCarryForward = useCallback(
    (documentIds: string[]) => {
      if (carryForwardByDefault) {
        markCarryForward(documentIds);
      }
    },
    [carryForwardByDefault, markCarryForward]
  );

  const queueLinkedToFacilities = useCallback(
    async (uploads: CreateDocumentInput[]) => {
      const { jobId, documentIds } = await createLinkedDocumentsAndQueueAnalysis({
        tenantId,
        uploads,
        facilityIds,
        upsertDocuments,
        queueAnalysis: async (ids) => {
          const { data } = await queueExistingAnalysis({
            variables: { tenantId, documentIds: ids },
          });
          return data?.queueDocumentAIEnrichmentJobExisting?.id;
        },
        deleteDocuments: (ids) =>
          Promise.all(ids.map((id) => deleteDocument({ variables: { id } }))),
      });
      handOffCarryForward(documentIds);
      return jobId;
    },
    [
      tenantId,
      facilityIds,
      upsertDocuments,
      queueExistingAnalysis,
      deleteDocument,
      handOffCarryForward,
    ]
  );

  const associations = useMemo(
    () => facilityIds.map((facilityId) => ({ tenantId, facilityId })),
    [tenantId, facilityIds]
  );

  return {
    associations,
    handOffCarryForward,
    queueLinkedToFacilities,
  };
}
